import { Alert, Button, Snackbar, Box } from '@mui/material'
import { usePageNavigation } from 'hooks/useNavigation'
import React from 'react'
import ToolBox from './ToolBox' // REUSED - same component as Instagram!
import {
  EditInstagramToolObject,
  ToolStatus,
  Platform,
  useChangeToolStatusMutation,
  useDeleteInstagramToolMutation,
  useEditInstagramToolMutation,
  useGetInstagramToolsQuery,
  useMediaAccountsQuery,
  useSaveYouTubeApiKeyMutation,
} from 'graphql/generated/graphql'
import Spinner from 'components/Spinner/Spinner'
import { Sparkles, Plus, Youtube } from 'lucide-react'
import YouTubeApiKeySetup from './YouTubeApiKeySetup' // NEW - API key setup
import YouTubeQuotaUsage from './YouTubeQuotaUsage' // NEW - quota display

export default function YouTubeAutomation(): JSX.Element {
  const { navigateToCreateAutomation } = usePageNavigation()
  
  // Get YouTube automations (filters by platform)
  const { data, loading, refetch } = useGetInstagramToolsQuery({
    fetchPolicy: 'network-only',
    variables: {
      platform: Platform.Youtube,
    },
  })
  
  // Get YouTube account
  const { data: mediaAccountsData, loading: accountsLoading, refetch: refetchAccounts } = useMediaAccountsQuery({
    variables: {
      platform: Platform.Youtube,
    },
  })

  const [saveApiKey] = useSaveYouTubeApiKeyMutation()
  const [statusSnackbar, setStatusSnackbar] = React.useState(false)
  const [deleteSnackbar, setDeleteSnackbar] = React.useState(false)
  const [editSnackBar, setEditSnackBar] = React.useState(false)
  const [errorSnackBar, setErrorSnackBar] = React.useState(false)
  const [apiKeySaved, setApiKeySaved] = React.useState(false)
  
  const [changeStatus] = useChangeToolStatusMutation()
  const [deleteTool] = useDeleteInstagramToolMutation()
  const [editInstagramTool] = useEditInstagramToolMutation()

  const youtubeAccount = mediaAccountsData?.mediaAccounts?.[0]

  if (loading || accountsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spinner />
      </Box>
    )
  }

  // Reused functions from InstagramAutomation.tsx
  const changeStatusFunc = async (status: boolean, id: number): Promise<void> => {
    const success = await changeStatus({
      variables: {
        status: status ? ToolStatus.Active : ToolStatus.Inactive,
        id,
      },
    })
    if (success) {
      setStatusSnackbar(true)
    } else {
      setErrorSnackBar(true)
    }
  }

  const deleteToolFunc = async (id: number): Promise<void> => {
    const deleted = await deleteTool({ variables: { id } })
    await refetch()
    if (deleted) {
      setDeleteSnackbar(true)
    } else {
      setErrorSnackBar(true)
    }
  }

  const editToolFunc = async (id: number, data: EditInstagramToolObject): Promise<void> => {
    const edited = await editInstagramTool({ variables: { id, data } })
    await refetch()
    if (edited) {
      setEditSnackBar(true)
    } else {
      setErrorSnackBar(true)
    }
  }

  const handleSaveApiKey = async (mediaAccountId: number, apiKey: string): Promise<void> => {
    try {
      const result = await saveApiKey({
        variables: { mediaAccountId, apiKey },
      })

      if (result.data?.saveYouTubeApiKey?.success) {
        setApiKeySaved(true)
        await refetchAccounts() // Refresh to update hasApiKey status
      } else {
        throw new Error(result.data?.saveYouTubeApiKey?.message ?? 'Failed to save API key')
      }
    } catch (error) {
      console.error('Error saving API key:', error)
      throw error
    }
  }

  // ========================================
  // STATE 1: No YouTube Account Connected
  // ========================================
  if (!youtubeAccount) {
    return (
      <Box sx={{ padding: '24px', minHeight: '100vh' }}>
        <Box
          sx={{
            maxWidth: '800px',
            margin: '0 auto',
            padding: '64px 32px',
            background: 'rgba(30, 41, 59, 0.4)',
            backdropFilter: 'blur(12px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 0, 0, 0.2)',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, rgba(255, 0, 0, 0.2), rgba(204, 0, 0, 0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <Youtube size={40} color="#ff0000" />
          </Box>
          <h2
            style={{
              margin: '0 0 12px 0',
              fontSize: '32px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ff0000, #cc0000)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Connect Your YouTube Account
          </h2>
          <p style={{ margin: '0 0 32px 0', fontSize: '16px', color: 'rgba(255, 255, 255, 0.6)' }}>
            To use YouTube automations, connect your YouTube account first.
          </p>
          <Button
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ff0000, #cc0000)',
              color: '#ffffff',
              borderRadius: '12px',
              padding: '12px 28px',
              fontSize: '15px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(255, 0, 0, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #cc0000, #990000)',
              },
            }}
            onClick={() => {
              window.location.href = '/accounts' // Update with your actual route
            }}
          >
            Connect YouTube Account
          </Button>
        </Box>
      </Box>
    )
  }

  // ========================================
  // STATE 2: No API Key Set Up
  // ========================================
  if (!youtubeAccount.hasApiKey) {
    return (
      <Box sx={{ padding: '24px', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: '32px' }}>
          <Box
            sx={{
              width: '56px',
              height: '56px',
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #ff0000, #cc0000)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(255, 0, 0, 0.4)',
            }}
          >
            <Youtube size={28} color="#ffffff" />
          </Box>
          <Box>
            <h2
              style={{
                margin: 0,
                fontSize: '36px',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #ff0000, #cc0000)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              YouTube Automations
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '16px', color: 'rgba(255, 255, 255, 0.6)' }}>
              Set up your API key to get started
            </p>
          </Box>
        </Box>

        {/* API Key Setup Component */}
        <YouTubeApiKeySetup
          mediaAccountId={youtubeAccount.id}
          existingApiKey={false}
          onSave={handleSaveApiKey}
        />
      </Box>
    )
  }

  // ========================================
  // STATE 3: Ready to use - Show automations list
  // ========================================
  const activeTools = data?.getInstagramTools.filter((tool) => tool?.status === 'Active').length ?? 0
  const totalRuns = data?.getInstagramTools.reduce((acc, tool) => acc + (tool?.triggered ?? 0), 0) ?? 0

  return (
    <Box sx={{ padding: '24px', minHeight: '100vh' }}>
      {/* Header Section - Same style as Instagram */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '32px',
          padding: '32px',
          background: 'rgba(30, 41, 59, 0.4)',
          backdropFilter: 'blur(12px)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 0, 0, 0.2)',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #ff0000, #cc0000)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(255, 0, 0, 0.4)',
              }}
            >
              <Youtube size={28} color="#ffffff" />
            </Box>
            <Box>
              <h2
                style={{
                  margin: 0,
                  fontSize: '36px',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #ff0000, #cc0000)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                YouTube Automations
              </h2>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#10b981',
                      boxShadow: '0 0 8px #10b981',
                    }}
                  />
                  <Box sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                    {activeTools} active
                  </Box>
                </Box>
                <Box sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>•</Box>
                <Box sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                  {totalRuns} total runs
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          sx={{
            background: 'linear-gradient(135deg, #ff0000, #cc0000)',
            color: '#ffffff',
            borderRadius: '12px',
            padding: '12px 28px',
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(255, 0, 0, 0.4)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #cc0000, #990000)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 16px rgba(255, 0, 0, 0.5)',
            },
          }}
          onClick={() => navigateToCreateAutomation('Youtube')} // Pass platform
        >
          Create Automation
        </Button>
      </Box>

      {/* Quota Usage - NEW component */}
      <Box sx={{ marginBottom: '32px' }}>
        <YouTubeQuotaUsage mediaAccountId={youtubeAccount.id} />
      </Box>

      {/* Empty State or Tools Grid - REUSES ToolBox.tsx */}
      {data?.getInstagramTools.length === 0 ? (
        <Box
          sx={{
            padding: '64px 32px',
            background: 'rgba(30, 41, 59, 0.3)',
            backdropFilter: 'blur(12px)',
            borderRadius: '20px',
            border: '2px dashed rgba(255, 0, 0, 0.3)',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, rgba(255, 0, 0, 0.2), rgba(204, 0, 0, 0.2))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <Sparkles size={40} color="#ff0000" />
          </Box>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '24px', fontWeight: 600, color: '#ffffff' }}>
            No YouTube Automations Yet
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.6)',
              maxWidth: '480px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Create your first YouTube automation to auto-reply to comments on your videos!
          </p>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: '24px',
          }}
        >
          {/* REUSES ToolBox.tsx - same component as Instagram! */}
          {data?.getInstagramTools.map((item) => {
            return (
              <ToolBox
                key={item?.id}
                title={item?.title ?? 'YouTube automation'}
                runs={item?.triggered ?? 0}
                active={item?.status === 'Active'}
                keyword={item?.keyword ?? ''}
                shouldReply={item?.shouldReply ?? false}
                commentReply={item?.commentReply ?? []}
                message={item?.message ?? []}
                statusMutation={changeStatusFunc}
                deleteMutation={deleteToolFunc}
                editMutation={editToolFunc}
                id={item?.id ?? 0}
              />
            )
          })}
        </Box>
      )}

      {/* Snackbars - Same as Instagram */}
      <Snackbar open={apiKeySaved} autoHideDuration={3000} onClose={() => setApiKeySaved(false)}>
        <Alert onClose={() => setApiKeySaved(false)} severity="success" variant="filled">
          YouTube API key saved successfully!
        </Alert>
      </Snackbar>
      <Snackbar open={statusSnackbar} autoHideDuration={2000} onClose={() => setStatusSnackbar(false)}>
        <Alert onClose={() => setStatusSnackbar(false)} severity="success" variant="filled">
          Tool status updated!
        </Alert>
      </Snackbar>
      <Snackbar open={deleteSnackbar} autoHideDuration={2000} onClose={() => setDeleteSnackbar(false)}>
        <Alert onClose={() => setDeleteSnackbar(false)} severity="error" variant="filled">
          Deleted tool successfully!
        </Alert>
      </Snackbar>
      <Snackbar open={editSnackBar} autoHideDuration={2000} onClose={() => setEditSnackBar(false)}>
        <Alert onClose={() => setEditSnackBar(false)} severity="info" variant="filled">
          Edited tool successfully!
        </Alert>
      </Snackbar>
      <Snackbar open={errorSnackBar} autoHideDuration={2000} onClose={() => setErrorSnackBar(false)}>
        <Alert onClose={() => setErrorSnackBar(false)} severity="error" variant="filled">
          Something went wrong!
        </Alert>
      </Snackbar>
    </Box>
  )
}